(() => {
  if (!customElements.get('oa-shipping-progress')) {
    customElements.define('oa-shipping-progress', class extends HTMLElement {
      connectedCallback() {
        const bar = this.querySelector('.free-shipping-bar__bar');
        const message = this.querySelector('.free-shipping-bar__message');
        if (!bar || !message) return;
        const sync = () => {
          const value = Math.min(100, Math.max(0, parseFloat(bar.style.getPropertyValue('--progress-width')) || 0));
          bar.setAttribute('aria-valuenow', String(Math.round(value * 100) / 100));
          bar.setAttribute('aria-valuetext', message.textContent.trim());
        };
        this.observer = new MutationObserver(sync);
        this.observer.observe(bar, {attributes:true, attributeFilter:['style']});
        this.observer.observe(message, {childList:true, characterData:true, subtree:true});
        sync();
      }
      disconnectedCallback() { this.observer?.disconnect(); }
    });
  }
  if (customElements.get('oa-cart-recommendations')) return;
  const cache = new Map();
  const impressions = new Set();
  const offer = 'halloween_cart_v1';
  const publish = (name, data, preview) => {
    const payload = {...data, offer, placement:'cart_drawer', preview};
    try {
      const result = window.Shopify?.analytics?.publish(name, payload);
      result?.catch?.(() => {});
    } catch (_) {}
    if (preview) console.info('[OA upsell preview]', name, payload);
  };
  class OACartRecommendations extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.timers = new Map();
      this.wrapper = this.closest('.quick-cart__wrapper');
      this.addEventListener('click', event => this.onClick(event), {signal:this.abort.signal});
      this.openObserver = new MutationObserver(() => this.onVisibility());
      if (this.wrapper) this.openObserver.observe(this.wrapper, {attributes:true, attributeFilter:['class']});
      document.addEventListener('visibilitychange', () => this.onVisibility(), {signal:this.abort.signal});
      this.onVisibility();
    }
    isOpen() { return this.wrapper?.classList.contains('active') && document.visibilityState === 'visible'; }
    clearTimers() {
      this.timers.forEach(timer => clearTimeout(timer));
      this.timers.clear();
    }
    onVisibility() {
      this.clearTimers();
      this.impressionObserver?.disconnect();
      if (!this.isOpen()) return;
      if (this.loaded) this.observeImpressions();
      else this.load();
    }
    disconnectedCallback() {
      this.abort.abort();
      this.openObserver.disconnect();
      this.impressionObserver?.disconnect();
      this.clearTimers();
    }
    dataFor(card) {
      return {
        product_id:card.dataset.productId,
        variant_id:card.dataset.variantId,
        source_product_id:this.dataset.productId,
        position:Array.from(this.querySelectorAll('.oa-rec')).indexOf(card) + 1,
        price:Number(card.dataset.price) / 100,
        currency:card.dataset.currency || 'USD'
      };
    }
    observeImpressions() {
      this.impressionObserver?.disconnect();
      this.impressionObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const card = entry.target;
          clearTimeout(this.timers.get(card));
          this.timers.delete(card);
          const key = `${this.dataset.productId}:${card.dataset.productId}:${card.dataset.variantId}`;
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || !this.isOpen() || impressions.has(key)) continue;
          this.timers.set(card, setTimeout(() => {
            this.timers.delete(card);
            if (!this.isConnected || !this.isOpen() || impressions.has(key)) return;
            impressions.add(key);
            publish('oa:upsell_impression', this.dataFor(card), this.dataset.preview === 'true');
            this.impressionObserver.unobserve(card);
          }, 1000));
        }
      }, {threshold:[0, 0.5]});
      this.querySelectorAll('.oa-rec').forEach(card => this.impressionObserver.observe(card));
    }
    async response(intent) {
      const url = new URL(this.dataset.url, location.origin);
      url.search = new URLSearchParams({section_id:'oa-cart-recommendations', product_id:this.dataset.productId, limit:'10', intent});
      const key = url.href;
      if (!cache.has(key)) {
        cache.set(key, fetch(key).then(response => {
          if (!response.ok) throw new Error('Recommendations unavailable');
          return response.text();
        }).catch(error => { cache.delete(key); throw error; }));
      }
      return new DOMParser().parseFromString(await cache.get(key), 'text/html').querySelectorAll('.oa-rec');
    }
    async load() {
      if (this.loading || this.loaded) return;
      this.loading = true;
      const excluded = new Set(this.dataset.cartIds.split(','));
      const cards = [];
      for (const intent of ['complementary','related']) {
        try {
          for (const card of await this.response(intent)) {
            if (!excluded.has(card.dataset.productId)) {
              excluded.add(card.dataset.productId);
              cards.push(card);
            }
            if (cards.length === 4) break;
          }
        } catch (_) {}
        if (cards.length === 4) break;
      }
      if (!this.isConnected) return;
      this.querySelector('.oa-recs').replaceChildren(...cards);
      this.toggleAttribute('data-ready', cards.length > 0);
      this.querySelector('.oa-rec-controls').hidden = cards.length <= 2;
      this.loaded = cards.length > 0;
      this.loading = false;
      if (this.isOpen()) this.observeImpressions();
    }
    async onClick(event) {
      const scroll = event.target.closest('[data-oa-scroll]');
      if (scroll) {
        const list = this.querySelector('.oa-recs');
        list.scrollBy({left:Number(scroll.dataset.oaScroll) * (list.clientWidth + 12), behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        return;
      }
      const card = event.target.closest('.oa-rec');
      if (!card) return;
      const button = event.target.closest('[data-oa-add]');
      const data = this.dataFor(card);
      const preview = this.dataset.preview === 'true';
      if (!button) {
        const link = event.target.closest('a');
        if (link) publish('oa:upsell_click', {...data, action:link.classList.contains('oa-rec-add') ? 'choose_options' : 'view_product'}, preview);
        return;
      }
      if (this.adding) return;
      this.adding = true;
      button.disabled = true;
      button.setAttribute('aria-busy','true');
      const addId = crypto.randomUUID();
      publish('oa:upsell_click', {...data, action:'add', add_id:addId, quantity:1}, preview);
      const status = this.querySelector('.oa-rec-status');
      status.textContent = '';
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart/add.js', {
          method:'POST', headers:{'Content-Type':'application/json'},
          body:JSON.stringify({items:[{id:button.dataset.oaAdd, quantity:1, properties:{
            _oa_source:'cart_upsell', _oa_offer:offer, _oa_add_id:addId,
            _oa_position:String(data.position), _oa_initial_quantity:'1', _oa_preview:preview ? 'true' : 'false'
          }}]})
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || this.dataset.error);
        const added = result.items?.find(item => String(item.id) === button.dataset.oaAdd && item.quantity > 0);
        if (added) publish('oa:upsell_add_success', {...data, add_id:addId, quantity:1, line_key:added.key}, preview);
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
      } catch (error) {
        status.textContent = error.message || this.dataset.error;
        publish('oa:upsell_add_error', {...data, add_id:addId, quantity:1}, preview);
      } finally {
        this.adding = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('oa-cart-recommendations', OACartRecommendations);
})();
